'use client';

import * as React from 'react';
import { LineChart as RechartsLineChart, Line } from 'recharts';
import { ChartContainer, type ChartConfig } from '@/hen-ui/shadcn/chart';
import { cn } from '@/hen-ui/utils';
import type { KPIDeltaSemantic } from '../KPIBanner';
import type { SeriesColorConfig } from '../chart-types';
import { evaluateColorRules } from '../chart-color-rules';

export interface SparklineChartProps {
  data: { x: number | string; y: number }[];
  semantic: KPIDeltaSemantic;
  lineStyle?: 'solid' | 'dashed';
  height?: number;
  className?: string;
  /**
   * Optional color configuration for the sparkline series. Rules and
   * `semanticColor` evaluate against the most recent (last) data point.
   * When active, overrides the default `semantic`-driven stroke.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
}

// Mirrors the success / error / subtext token pattern used by WaterfallChart and LineChart.
const semanticStroke: Record<KPIDeltaSemantic, string> = {
  positive: 'var(--success-500, #0D7D4D)',
  negative: 'var(--error-500, #D23228)',
  neutral: 'var(--subtext-color, #6B7280)',
};

/**
 * Decorative sparkline — mini inline line chart intended for use inside a
 * KPI tile when `statStyle="sparkline"`. No axes, grid, legend, or tooltip.
 * Stroke color follows the KPI delta semantic via design tokens.
 *
 * Sizing: the outer div carries an explicit pixel `height` so ChartContainer
 * (which uses `h-full`) can fill it. Matches the sizing pattern used by
 * LineChart and WaterfallChart.
 */
export const SparklineChart: React.FC<SparklineChartProps> = ({
  data,
  semantic,
  lineStyle = 'solid',
  height = 32,
  className,
  seriesColorConfigs,
}) => {
  if (!data.length) return null;

  const colorCfg = seriesColorConfigs?.['y'];
  const lastValue = data[data.length - 1]?.y ?? null;
  const resolvedStroke =
    colorCfg &&
    ((colorCfg.colorRules && colorCfg.colorRules.length > 0) ||
      (colorCfg.semanticColor !== undefined && colorCfg.semanticColor !== null))
      ? evaluateColorRules(
          colorCfg.colorRules,
          lastValue,
          colorCfg.semanticColor,
          semanticStroke[semantic],
        )
      : semanticStroke[semantic];

  const chartConfig: ChartConfig = {
    y: { label: 'Value', color: resolvedStroke },
  };

  return (
    <div
      className={cn('min-w-0 w-full', className)}
      aria-hidden='true'
      data-sparkline=''
      data-semantic={semantic}
      style={{ height }}
    >
      <ChartContainer
        config={chartConfig}
        className='aspect-auto h-full w-full'
      >
        <RechartsLineChart
          data={data}
          margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
        >
          <Line
            type='monotone'
            dataKey='y'
            stroke='var(--color-y)'
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            strokeDasharray={lineStyle === 'dashed' ? '4 2' : undefined}
          />
        </RechartsLineChart>
      </ChartContainer>
    </div>
  );
};

SparklineChart.displayName = 'SparklineChart';
